import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '@/redux/core'
import { selectPackages } from '@/redux/package/package.selector'
import { PackageInfo } from '@/redux/package/package.types'
import { ROUTES, isBookseatsEventType } from '../constants'

const usePackageInfo = (): UsePackageInfoReturn => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const packages = useAppSelector(selectPackages)
  const sessionId = searchParams.get('sessionId') ?? ''
  const modify = searchParams.get('modify') ?? undefined
  const currentPackage = packages[sessionId] ?? undefined

  const isPackage = useMemo(
    () => !!currentPackage?.cartInfo?.ticket,
    [currentPackage?.cartInfo?.ticket],
  )

  const productCount = useMemo(() => {
    return getProductCount(currentPackage)
  }, [currentPackage])

  const isTicketModify = useMemo(
    () => modify === 'true' && !!currentPackage?.cartInfo?.ticket,
    [modify, currentPackage?.cartInfo?.ticket],
  )

  const isHotelModify = useMemo(
    () => modify === 'true' && !!currentPackage?.cartInfo?.hotel,
    [modify, currentPackage?.cartInfo?.hotel],
  )

  const isFlightModify = useMemo(
    () => modify === 'true' && !!currentPackage?.cartInfo?.flight,
    [modify, currentPackage?.cartInfo?.flight],
  )

  return {
    isPackage,
    modify,
    isTicketModify,
    isHotelModify,
    isFlightModify,
    currentPackage,
    isInsidePackageFlow: Boolean(sessionId) || location.pathname.startsWith(ROUTES.PACKAGE.BASE),
    productCount,
    isSingleProduct: productCount === 1,
    isTicketInCart: !!currentPackage?.cartInfo?.ticket,
    isAddonsInCart: (currentPackage?.cartInfo?.addons?.length ?? 0) > 0,
    isHotelInCart: !!currentPackage?.cartInfo?.hotel,
    isFlightInCart: !!currentPackage?.cartInfo?.flight,
    isFreePurchase: !!currentPackage?.cartInfo?.pricing?.freePurchase,
    shouldSkipTickets: isBookseatsEventType(currentPackage?.packageConfig?.eventType),
  }
}

export function getProductCount(packageInfo?: PackageInfo) {
  let counter = 0
  const addonCount = packageInfo?.cartInfo?.addons?.length ?? 0

  if (packageInfo?.cartInfo?.ticket) counter += 1
  if (packageInfo?.cartInfo?.hotel) counter += 1
  if (packageInfo?.cartInfo?.flight) counter += 1
  counter += addonCount
  return counter
}

interface UsePackageInfoReturn {
  /**
   * A boolean variable that indicates whether the current entity is a package.
   * If true, it represents that the entity is categorized or identified as a package.
   * If false, it implies that the entity is not considered a package.
   */
  isPackage: boolean

  /**
   * The current package info based on the sessionId in the URL
   */
  currentPackage?: PackageInfo

  /**
   * Boolean indicating if user is inside package flow
   */
  isInsidePackageFlow: boolean

  /**
   * Number of products in the package
   */
  productCount: number

  /**
   * A string variable used to represent the modification or changes applied on ticket and hotel
   */
  modify?: string

  /**
   * A boolean variable that specifies whether a ticket can be modified.
   * When set to true, the ticket is eligible for changes or updates.
   * When set to false, the ticket cannot be altered.
   */
  isTicketModify: boolean

  /**
   * A boolean variable that specifies whether a hotel can be modified.
   * When set to true, the hotel is eligible for changes or updates.
   * When set to false, the hotel cannot be altered.
   */
  isHotelModify: boolean

  /**
   * Represents the modification status of a flight.
   *
   * This variable indicates whether the flight details can be modified or not.
   * A value of `true` means the flight is eligible for modifications,
   * while a value of `false` signifies that the flight details are locked and cannot be changed.
   */
  isFlightModify: boolean

  /**
   * Indicates whether the context or operation pertains to a single product.
   *
   * When set to `true`, it implies that the current operation, view, or logic is specifically handling a single product instance.
   * If `false`, it suggests handling of multiple products or no specific product at all.
   */
  isSingleProduct: boolean

  /**
   * Indicates whether a ticket is currently present in the user's shopping cart.
   * This variable is a boolean, where `true` signifies that the ticket is in the cart,
   * and `false` indicates that the ticket is not in the cart.
   */
  isTicketInCart: boolean

  /**
   * Indicates whether any add-ons have been added to the shopping cart.
   */
  isAddonsInCart: boolean

  /**
   * Indicates whether a hotel is currently present in the shopping cart.
   * It is a boolean value where:
   * - true signifies that a hotel has been added to the cart.
   * - false signifies that no hotel is present in the cart.
   */
  isHotelInCart: boolean

  /**
   * Indicates whether a flight has been added to the shopping cart.
   *
   * This variable is used to track the presence of a flight selection within
   * the user's cart, enabling conditional logic for cart management and
   * checkout processes.
   */
  isFlightInCart: boolean

  /**
   * Indicates whether the cart pricing is a free purchase (no payment required).
   * When `true`, the checkout skips the Stripe payment flow and books the package
   * without a payment method.
   */
  isFreePurchase: boolean

  /**
   * Indicates whether the ticket selection step should be skipped based on the package event type.
   */
  shouldSkipTickets: boolean
}

export default usePackageInfo
